import React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';

import { hasFeature } from 'root/api/admin/features';
import { createReactRenderer } from 'root/apps/helpers/react-renderer';
import { toBoolean } from 'root/apps/pages/utils/sanitize';

import { formatCurrency } from '../ShipmentHistory/utils/format';
import { StyledBodyText, StyledHeaderText, StyledModal } from './Modal.style';
import { waitForVariable } from './utils';

import type { AddToInventoryOverview } from '../ShipmentHistory/types';

interface Subtotal {
  label: string;
  value: number;
  currency: string;
}

interface ModalBypassProps {
  callback?: (result: boolean) => void;
  unmount?: () => void;
}

const ModalBypass: React.FC<ModalBypassProps> = ({ callback, unmount }) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasItems, sethasItems] = React.useState(false);
  const [data, setData] = React.useState({} as AddToInventoryOverview);
  const [isError, setIsError] = React.useState(false);
  const [hasLandCostFeature, setHasLandCostFeature] = React.useState(false);
  const [hasProductCostPermission] = React.useState<boolean | null>(
    toBoolean(window.merchantos.session.sessData?.Rights?.product_cost)
  );

  const {
    sumQty,
    sumVendorCosts,
    sumTotalVendorCosts,
    sumCosts,
    sumTotalCosts,
    sumShippingCosts,
    sumShippingVendorCosts,
    sumDiscountMoneyValue,
    sumDiscountMoneyVendorValue,
    vendorCurrencyCode,
    defaultCurrencyCode,
  } = data;
  const { id: orderId } = window.history.getFilters();

  const fetchHasFeature = async () => {
    if (await hasFeature('PurchaseOrderLandedCostsSpreadCosts')) {
      setHasLandCostFeature(true);
    }
  };

  const handleAdditems = () => {
    setIsOpen(false);
    if (callback) callback(true);
    if (unmount) unmount();
  };

  const handleDismiss = () => {
    setIsOpen(false);
    if (callback) callback(false);
    if (unmount) unmount();
  };

  const handleSubtotalDisplay = (subtotal: number | string, currencyCode: string) => {
    const convertedTotal = formatCurrency(subtotal);
    const displaySubtotal = `${
      currencyCode && vendorCurrencyCode !== defaultCurrencyCode ? ` (${currencyCode})` : ''
    }: ${convertedTotal}`;

    return displaySubtotal;
  };

  const SubtotalDisplay = ({ label, value, currency }: Subtotal) => {
    if (value === null || value === undefined) return <></>;
    return (
      <StyledBodyText size="small" fontWeight="bold">
        {label}
        <var>{handleSubtotalDisplay(value, currency)}</var>
      </StyledBodyText>
    );
  };

  const getShipmentOverviewData = async () => {
    await waitForVariable(() => !window.isPOReceivdQtyBeingUpdated);

    try {
      const res = await fetch(`/admin/order/${orderId}/shipment/overview/`);
      const { data } = await res.json();
      setData(data);
      sethasItems(data.sumQty > 0);
    } catch (err) {
      setIsError(true);
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    getShipmentOverviewData();
    fetchHasFeature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={handleDismiss}
      data-test="received-inventory-modal"
    >
      {isError ? (
        <Alert type="danger" noCloseBtn data-test="api-error-alert" mb={2}>
          An error happened ...
        </Alert>
      ) : null}
      {isLoading ? (
        <>
          <ModalHeader data-test="received-inventory-modal-loading">
            <Bone height={3} width="50%" />
          </ModalHeader>
          <ModalBody style={{ paddingTop: '12px', paddingBottom: '12px' }}>
            <Flex flexDirection="column">
              <Bone height={3} width="100%" style={{ marginBottom: '20px' }} />
              <Flex flexDirection="column">
                <Bone height="14px" width="20%" style={{ marginBottom: '4px' }} />
                {hasProductCostPermission ? (
                  <>
                    <Bone height="14px" width="20%" style={{ marginBottom: '4px' }} />
                    <Bone height="14px" width="20%" style={{ marginBottom: '4px' }} />
                  </>
                ) : null}
              </Flex>
            </Flex>
          </ModalBody>
        </>
      ) : null}
      {!isLoading && !hasItems ? (
        <>
          <ModalHeader data-test="received-inventory-modal-no-item">
            <StyledHeaderText size="normal">No items added to inventory</StyledHeaderText>
          </ModalHeader>
          <ModalBody style={{ paddingTop: '12px', paddingBottom: '12px' }}>
            <StyledHeaderText size="small">
              Add the item quantity in the Received column of the table.
            </StyledHeaderText>
          </ModalBody>
        </>
      ) : null}
      {!isLoading && hasItems ? (
        <>
          <ModalHeader data-test="received-inventory-modal-has-items">
            <StyledHeaderText>
              You&apos;re about to add items to inventory and create a record of shipment
            </StyledHeaderText>
          </ModalHeader>
          <ModalBody style={{ paddingTop: '12px', paddingBottom: '12px' }}>
            <Flex flexDirection="column">
              <StyledBodyText size="small" pb={3}>
                Make sure the quantity and unit cost is correct before adding the items.
              </StyledBodyText>
              <Flex flexDirection="column">
                <StyledBodyText size="small" fontWeight="bold" data-test="sumQty-text">
                  Quantity:&nbsp;
                  <var>{sumQty || '-'}</var>
                  &nbsp;
                  {sumQty > 1 ? 'items' : 'item'}
                </StyledBodyText>
                {hasProductCostPermission ? (
                  // hide cost if user doesn't have permission
                  <>
                    {vendorCurrencyCode !== defaultCurrencyCode ? (
                      <>
                        <SubtotalDisplay
                          label="Subtotal"
                          value={sumVendorCosts}
                          currency={vendorCurrencyCode}
                        />
                        <>
                          {hasLandCostFeature ? (
                            <>
                              <SubtotalDisplay
                                label="Shipping"
                                value={sumShippingVendorCosts}
                                currency={vendorCurrencyCode}
                              />
                            </>
                          ) : null}
                        </>
                        <SubtotalDisplay
                          label="Discount"
                          value={sumDiscountMoneyVendorValue}
                          currency={vendorCurrencyCode}
                        />
                        <SubtotalDisplay
                          label="Total"
                          value={sumTotalVendorCosts}
                          currency={vendorCurrencyCode}
                        />
                        <SubtotalDisplay
                          label="Total"
                          value={sumTotalCosts}
                          currency={defaultCurrencyCode}
                        />
                      </>
                    ) : (
                      <>
                        <SubtotalDisplay
                          label="Subtotal"
                          value={sumCosts}
                          currency={vendorCurrencyCode}
                        />
                        <>
                          {hasLandCostFeature ? (
                            <>
                              <SubtotalDisplay
                                label="Shipping"
                                value={sumShippingCosts}
                                currency={vendorCurrencyCode}
                              />
                            </>
                          ) : null}
                        </>
                        <SubtotalDisplay
                          label="Discount"
                          value={sumDiscountMoneyValue}
                          currency={vendorCurrencyCode}
                        />
                        <SubtotalDisplay
                          label="Total"
                          value={sumTotalCosts}
                          currency={defaultCurrencyCode}
                        />
                      </>
                    )}
                  </>
                ) : null}
              </Flex>
            </Flex>
          </ModalBody>
        </>
      ) : null}
      <ModalFooter>
        <Flex justifyContent="end">
          <Button onClick={handleDismiss}>Cancel</Button>
          <Button disabled={!hasItems} fill variant="secondary" ml={2} onClick={handleAdditems}>
            Add items
          </Button>
        </Flex>
      </ModalFooter>
    </StyledModal>
  );
};

export { ModalBypass };

interface SetModalBypassProps {
  callback?: ModalBypassProps['callback'];
}
export const setModalBypass = (setModalBypassProps: SetModalBypassProps) => {
  const RenderedComponent = createReactRenderer({
    component: ModalBypass,
    selector: '[data-react-root="ReactModal"]',
  });
  const props = { ...setModalBypassProps, unmount: RenderedComponent.unmount };
  RenderedComponent.mount({ props });
};
